import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Cloud, Loader2, Save, ShieldCheck, Copy } from 'lucide-react';
import api from '../../services/api';

const STEPS = [
  'Google Cloud Console → pick or create a project; note its Project ID.',
  "Cloud Storage → Buckets → Create. Name it (e.g. the academy slug), choose 'Uniform' access control and keep 'Public access prevention' ON. Parent documents stay private.",
  "IAM & Admin → Service Accounts → Create service account (e.g. 'vyomos-storage'). No project role is needed.",
  "Open the bucket → Permissions → Grant access → paste the service account's email → role 'Storage Object User' → Save.",
  'Back in Service Accounts → the account → Keys → Add key → Create new key → JSON. A file downloads; paste its whole contents below.',
  'Optional folder: files go inside this folder in the bucket (e.g. 2026).',
  "Save → Test access → switch on. Then 'Copy existing files' moves earlier uploads over; downloads switch only after each copy is checked.",
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10';

const Migration = ({ m }) => {
  if (!m) return null;
  return (
    <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2" data-testid="storage-migration-status">
      Copy existing files: <strong>{m.status}</strong>
      {m.status === 'done' && <> · {m.copied} copied · {m.skipped} already moved · {m.failed} failed</>}
      {(m.errors || []).slice(0, 3).map((e) => <div key={e} className="text-rose-600 font-mono truncate">{e}</div>)}
    </div>
  );
};

export function TenantStorageCard({ tenantId }) {
  const [s, setS] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState('');
  const url = `/super-admin/tenants/${tenantId}/storage`;
  const load = useCallback(() => api.get(url).then((r) => setS(r.data)).catch(() => toast.error('Could not load storage settings')), [url]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = async (what, fn) => {
    setBusy(what);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
    load();
  };
  const save = () => run('save', async () => {
    await api.put(url, { enabled: s.enabled, project_id: s.project_id, bucket: s.bucket, folder: s.folder, service_account_json: key || null });
    setKey('');
    toast.success('Storage settings saved');
  });
  const test = () => run('test', async () => {
    const { data } = await api.post(`${url}/test`);
    data.ok ? toast.success('Bucket access OK') : toast.error(data.reason || 'Access failed');
  });
  const migrate = () => run('migrate', async () => {
    await api.post(`${url}/migrate`);
    toast.success('Copy started — refresh in a minute to see progress');
  });

  if (!s) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="integ-card-gcs">
      <div className="flex items-center gap-2 flex-wrap">
        <Cloud className="w-4 h-4 text-slate-500" />
        <h3 className="font-semibold text-slate-800">Google Cloud Storage (documents)</h3>
        <span className={`text-xs px-2 py-0.5 rounded border ${s.enabled ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`} data-testid="storage-status">
          {s.enabled ? 'on' : 'off'}
        </span>
        {s.last_tested_at && (
          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
            {s.last_ok ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3 text-amber-600" />}
            tested {s.last_tested_at.slice(0, 16).replace('T', ' ')}{s.last_error ? ` · ${s.last_error}` : ''}
          </span>
        )}
      </div>
      <details className="text-sm text-slate-600" data-testid="storage-instructions">
        <summary className="cursor-pointer font-medium text-slate-700">Setup instructions (do this at onboarding)</summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1">{STEPS.map((t) => <li key={t}>{t}</li>)}</ol>
      </details>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Project ID"><input value={s.project_id} onChange={set('project_id')} className={inputCls} data-testid="storage-project-id" /></Field>
        <Field label="Private bucket name"><input value={s.bucket} onChange={set('bucket')} className={inputCls} data-testid="storage-bucket" /></Field>
        <Field label="Folder (optional)"><input value={s.folder} onChange={set('folder')} placeholder="2026" className={inputCls} data-testid="storage-folder" /></Field>
      </div>
      <Field label={s.has_key ? `Service account key — saved (${s.client_email}). Paste a new one to replace it.` : 'Service account key (JSON)'}>
        <textarea rows={3} value={key} onChange={(e) => setKey(e.target.value)} placeholder='{"type": "service_account", ...}' className={`${inputCls} font-mono text-xs`} data-testid="storage-key" />
      </Field>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={s.enabled} onChange={set('enabled')} data-testid="storage-enabled" />
        Store new document uploads in this bucket
      </label>
      <Migration m={s.migration} />
      <div className="flex flex-wrap gap-2">
        <Btn onClick={save} busy={busy === 'save'} icon={Save} tid="storage-save">Save</Btn>
        <Btn onClick={test} busy={busy === 'test'} icon={ShieldCheck} tid="storage-test" disabled={!s.has_key}>Test access</Btn>
        <Btn onClick={migrate} busy={busy === 'migrate'} icon={Copy} tid="storage-migrate" disabled={!s.enabled}>Copy existing files</Btn>
      </div>
    </div>
  );
}

const Btn = ({ onClick, busy, icon: Icon, tid, disabled, children }) => (
  <button type="button" onClick={onClick} disabled={busy || disabled} data-testid={tid}
    className="text-xs px-3 py-1.5 border border-slate-300 rounded inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40">
    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}{children}
  </button>
);

export default TenantStorageCard;
